import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Edit, CalendarIcon, ChevronDown, ArrowUpDown, X } from 'lucide-react';
import { MilestoneItem, Child, getStoredMilestones, storeMilestones, getStoredChildren, updateMilestone } from '@/utils/storage';
import { Dialog, DialogTrigger, DialogContent, DialogTitle, DialogDescription, DialogFooter, DialogHeader } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import ShareMemory from '@/components/memories/ShareMemory';
import { Memory } from '@/types/memory';
import { useIsMobile } from '@/hooks/use-mobile';
import { logger } from '@/utils/logger';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const Milestones = () => {
  const [milestones, setMilestones] = useState<MilestoneItem[]>([]);
  const [filteredMilestones, setFilteredMilestones] = useState<MilestoneItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [children, setChildren] = useState<Child[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentMilestoneId, setCurrentMilestoneId] = useState<string | null>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();

  const [content, setContent] = useState("");
  const [context, setContext] = useState("");
  const [selectedChild, setSelectedChild] = useState("");
  const [date, setDate] = useState<Date>(new Date());

  // Filter states
  const [filterChildId, setFilterChildId] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const fetchedMilestones = await getStoredMilestones();
        setMilestones(fetchedMilestones);
        setFilteredMilestones(fetchedMilestones);

        const fetchedChildren = await getStoredChildren();
        setChildren(fetchedChildren);

        if (fetchedChildren.length > 0 && !selectedChild) {
          setSelectedChild(fetchedChildren[0].id);
        }
      } catch (error) {
        logger.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter and sort milestones
  useEffect(() => {
    let filtered = [...milestones];

    // Filter by child
    if (filterChildId) {
      filtered = filtered.filter(milestone => milestone.childId === filterChildId);
    }

    // Filter by date range
    if (dateFrom) {
      filtered = filtered.filter(milestone => new Date(milestone.date) >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(milestone => new Date(milestone.date) <= dateTo);
    }

    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

    setFilteredMilestones(filtered);
  }, [filterChildId, dateFrom, dateTo, sortOrder, milestones]);
  
  const resetForm = () => {
    setContent("");
    setContext("");
    setDate(new Date());
    setIsEditing(false);
    setCurrentMilestoneId(null);
  };
  
  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };
  
  const openEditDialog = (milestone: MilestoneItem) => {
    setContent(milestone.content);
    setContext(milestone.context || "");
    setSelectedChild(milestone.childId);
    setDate(new Date(milestone.date));
    setIsEditing(true);
    setCurrentMilestoneId(milestone.id);
    setDialogOpen(true);
  };
  
  const handleSaveMilestone = async () => {
    if (!content.trim() || !selectedChild) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }
    
    try {
      if (isEditing && currentMilestoneId) {
        const updatedMilestone = await updateMilestone({
          id: currentMilestoneId,
          content,
          childId: selectedChild,
          date: date.toISOString().split('T')[0],
          context: context.trim() || undefined,
          childName: ""
        });
        
        if (updatedMilestone) {
          setMilestones(milestones.map(m =>
            m.id === updatedMilestone.id ? updatedMilestone : m
          ));
        }
      } else {
        const newMilestone = await storeMilestones({
          content,
          childId: selectedChild,
          date: date.toISOString().split('T')[0],
          context: context.trim() || undefined
        });
        
        if (newMilestone) {
          setMilestones([newMilestone, ...milestones]);
        }
      }
      
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      logger.error("Error saving milestone:", error);
      toast({
        title: "Error",
        description: "There was a problem saving your milestone",
        variant: "destructive"
      });
    }
  };
  
  const milestoneToMemory = (milestone: MilestoneItem): Memory => {
    return {
      id: milestone.id,
      childId: milestone.childId,
      childName: milestone.childName,
      content: milestone.content,
      date: milestone.date,
      context: milestone.context,
      type: 'milestone'
    };
  };

  const getCurrentFilterLabel = () => {
    if (!filterChildId) return "All Milestones";
    const selectedChild = children.find(child => child.id === filterChildId);
    return selectedChild ? selectedChild.name : "All Milestones";
  };

  const clearAllFilters = () => {
    setFilterChildId(null);
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const hasActiveFilters = filterChildId || dateFrom || dateTo;

  return (
    <Layout>
      <div className="container max-w-4xl px-2 md:px-4 pb-16 md:pb-0">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-left">Milestones</h1>
            <p className="text-gray-500 text-sm text-left">Record your children's important developmental steps</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button onClick={openAddDialog} className="rounded-full bg-primary text-white hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                Add Milestone
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] md:max-w-md">
              <DialogHeader>
                <DialogTitle>{isEditing ? "Edit Milestone" : "Add New Milestone"}</DialogTitle>
                <DialogDescription>
                  {isEditing 
                    ? "Update this milestone in your child's development."
                    : "Record an important achievement or development in your child's life."
                  }
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="child">Child</Label>
                  <Select value={selectedChild} onValueChange={setSelectedChild}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a child" />
                    </SelectTrigger>
                    <SelectContent>
                      {children.map(child => (
                        <SelectItem key={child.id} value={child.id}>
                          {child.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="date">Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-normal",
                          !date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, "PPP") : "Select a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => d && setDate(d)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="milestone">Milestone</Label>
                  <Textarea
                    id="milestone"
                    placeholder="e.g., First steps, First word"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                  />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="context">Context (Optional)</Label>
                  <Textarea
                    id="context"
                    placeholder="Any additional details about this milestone"
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                  />
                </div>
              </div>
              
              <DialogFooter className={cn(
                isMobile && "flex-col gap-2"
              )}>
                <Button variant="outline" onClick={() => {
                  resetForm();
                  setDialogOpen(false);
                }} className={cn(
                  isMobile && "w-full"
                )}>
                  Cancel
                </Button>
                <Button onClick={handleSaveMilestone} className={cn(
                  isMobile && "w-full"
                )}>
                  {isEditing ? "Update" : "Add"} Milestone
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {/* Child Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <span>{getCurrentFilterLabel()}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuLabel>Filter by Child</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setFilterChildId(null)}
                className={!filterChildId ? "bg-accent" : ""}
              >
                All Milestones
              </DropdownMenuItem>
              {children.map(child => (
                <DropdownMenuItem
                  key={child.id}
                  onClick={() => setFilterChildId(child.id)}
                  className={filterChildId === child.id ? "bg-accent" : ""}
                >
                  {child.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Date From Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !dateFrom && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateFrom ? format(dateFrom, "PPP") : "From date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={dateFrom}
                onSelect={setDateFrom}
                initialFocus
              />
              {dateFrom && (
                <div className="p-3 border-t">
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => setDateFrom(undefined)}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* Date To Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !dateTo && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateTo ? format(dateTo, "PPP") : "To date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={dateTo}
                onSelect={setDateTo}
                initialFocus
              />
              {dateTo && (
                <div className="p-3 border-t">
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => setDateTo(undefined)}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>

          {/* Sort Order */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <ArrowUpDown className="h-3.5 w-3.5" />
                <span>{sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Sort By</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setSortOrder('newest')}
                className={sortOrder === 'newest' ? "bg-accent" : ""}
              >
                Newest First
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setSortOrder('oldest')}
                className={sortOrder === 'oldest' ? "bg-accent" : ""}
              >
                Oldest First
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              className="flex items-center gap-1.5"
            >
              <X className="h-3.5 w-3.5" />
              Clear Filters
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="text-center py-10">
            <p>Loading milestones...</p>
          </div>
        ) : filteredMilestones.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-10">
              <p className="text-center text-gray-500 mb-4">
                {filterChildId || dateFrom || dateTo
                  ? "No milestones found matching your filters."
                  : "You haven't recorded any milestones yet."}
              </p>
              <Button onClick={openAddDialog}>
                <Plus className="w-4 h-4 mr-2" />
                {filterChildId || dateFrom || dateTo ? "Add Milestone" : "Add Your First Milestone"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredMilestones.map(milestone => (
              <Card key={milestone.id}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CalendarIcon className="h-4 w-4 text-primary" />
                    <span>{milestone.childName}</span>
                    <span className="text-gray-500 text-sm ml-auto font-normal">
                      {new Date(milestone.date).toLocaleDateString()}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="italic text-left">
                    {milestone.content}
                  </p>
                  {milestone.context && (
                    <p className="text-sm text-gray-500 mt-1 text-left">
                      {milestone.context}
                    </p>
                  )}
                </CardContent>
                <CardFooter className="flex justify-start space-x-2 pt-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditDialog(milestone)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <ShareMemory memory={milestoneToMemory(milestone)} />
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Milestones;
